// Mobile-only — compact "search ran" card that replaces the full
// FlightSearchWidget at the top of /search. Tapping the pencil opens
// a full-screen dialog with the editable widget.

import { useEffect, useState } from "react";
import { ArrowsLeftRight, PencilSimple, User, X } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import FlightSearchWidget from "@/components/FlightSearchWidget";
import { api } from "@/lib/api";

// Tiny in-memory cache populated once per session — the suggestions
// endpoint returns the full curated airport list when called with no
// query, so one fetch gives us city names for every IATA we care about.
let AIRPORT_CACHE = null;
async function loadAirports() {
  if (AIRPORT_CACHE) return AIRPORT_CACHE;
  try {
    const { data } = await api.get("/products/places/suggestions?q=");
    AIRPORT_CACHE = Object.fromEntries((data || []).map((a) => [a.code, a]));
  } catch { AIRPORT_CACHE = {}; }
  return AIRPORT_CACHE;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function cabinPretty(c) {
  if (!c) return "Economy";
  return c.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function MobileSearchSummary({ query }) {
  const [open, setOpen] = useState(false);
  const [airports, setAirports] = useState(AIRPORT_CACHE || {});

  useEffect(() => {
    if (AIRPORT_CACHE) return;
    let alive = true;
    loadAirports().then((m) => alive && setAirports(m));
    return () => { alive = false; };
  }, []);

  const cityForCode = (code) => {
    if (!code) return "";
    const a = airports[code.toUpperCase()];
    return a?.city || code;
  };

  const total = (query.adults || 0) + (query.children || 0) + (query.infants || 0) || 1;
  const dateLine = query.return_date
    ? `${fmtDate(query.depart_date)} – ${fmtDate(query.return_date)}`
    : fmtDate(query.depart_date);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="mobile-search-summary"
        className="w-full bg-white border border-[#E0DDD6] rounded-2xl p-4 text-left flex items-center gap-3 shadow-sm"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[#1C1C1C] font-semibold text-base">
            <span className="truncate">{cityForCode(query.origin)}</span>
            <ArrowsLeftRight size={14} weight="bold" className="text-[#C29B62] shrink-0" />
            <span className="truncate">{cityForCode(query.destination)}</span>
          </div>
          <p className="text-xs text-[#666460] mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{dateLine}</span>
            <span className="text-[#E0DDD6]">·</span>
            <span className="inline-flex items-center gap-1">
              {total} <User size={11} weight="fill" className="text-[#666460]" />
            </span>
            <span className="text-[#E0DDD6]">·</span>
            <span>{cabinPretty(query.cabin)}</span>
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-center gap-1">
          <span
            data-testid="mobile-search-edit-btn"
            aria-label="Edit search"
            className="w-9 h-9 flex items-center justify-center text-[#C29B62] border border-[#E0DDD6] rounded-full"
          >
            <PencilSimple size={14} weight="bold" />
          </span>
          <span className="text-[9px] tracking-[0.14em] uppercase text-[#C29B62] font-semibold leading-none">
            Modify
          </span>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-none w-screen h-screen md:h-auto md:max-w-2xl bg-[#0A0A0B] border-0 rounded-none p-5 overflow-y-auto"
          data-testid="mobile-search-edit-dialog"
        >
          <div className="flex items-center justify-between mb-5">
            <p className="tcp-overline text-[#C29B62]">Modify search</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-[#F7F5F0] p-2 -mr-2"
            >
              <X size={22} weight="bold" />
            </button>
          </div>
          <FlightSearchWidget
            initialOrigin={query.origin}
            initialDestination={query.destination}
            initialCabin={query.cabin}
            initialTripType={query.trip_type}
            initialDepart={query.depart_date}
            initialReturn={query.return_date}
            initialAdults={query.adults}
            initialChildren={query.children}
            initialInfants={query.infants}
            onSubmitted={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
